import { useMemo } from 'react';
import useRoleplayHelper from 'features/realplays/RealPlay.helper';
import useReviewScenarioMutation from 'features/realplays/services/mutations/useReviewScenario';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import RealPlayRecordDataHelper from 'features/realplays/RealPlayRecordData.helper';
import RealPlayQualifiedDataHelper from 'features/realplays/RealPlayQualifiedData.helper';

const RealPlayQualifiedLogic = () => {
  const { navigate, PATHS } = useRoleplayHelper();
  const { uuid } = useParams();
  const history = useHistory();
  let location = useLocation();

  const { mutate: reviewScenario } = useReviewScenarioMutation();

  const {
    assignment_uuid,
    scenarios,
    selectedScenario,
    endDate,
    title,
    setScenarioWithSelector,
    reset,
    self_assigned,
    assigned_to_user,
    customer_context,
    client,
  } = RealPlayRecordDataHelper();

  const { final_decision, rating_criteria, comment, summary, status_ai, isEditing } =
    RealPlayQualifiedDataHelper();

  const scenarioSelectedIndex = useMemo(
    () => scenarios?.findIndex((scenario) => scenario.uuid === selectedScenario?.uuid),
    [scenarios, selectedScenario]
  );

  const criteria_ratings = useMemo(() => {
    return rating_criteria?.map((r) => ({
      stars: r?.rating,
      uuid: r?.uuid,
      reasoning: r?.reasoning,
      improvement_suggestion: r?.improvement_suggestion,
    }));
  }, [rating_criteria]);

  const formatDate = () => {
    const dateString = new Date();

    let month = dateString.getUTCMonth() + 1;
    if (month < 10) month = `0${month}`;
    let day = dateString.getUTCDate();
    if (day < 10) day = `0${day}`;
    let year = dateString.getUTCFullYear();

    const newDate = day + '-' + month + '-' + year;
    return newDate;
  };

  const formattedDate = () => {
    if (endDate) {
      const date = new Date(endDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Los meses empiezan en 0
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return formatDate();
  };

  const options = useMemo(() => {
    if (!scenarios || scenarios?.length === 0) return [];
    return scenarios?.map((sc, i) => ({
      label: `${i + 1}. ${sc?.name}`,
      value: sc?.uuid,
    }));
  }, [scenarios]);
  const scenariosLength = useMemo(() => scenarios?.length || 0, [scenarios]);

  const value = useMemo(() => {
    return (options ?? []).find((opt) => opt?.value === selectedScenario?.uuid);
  }, [options, selectedScenario]);

  const onSelectedValue = (value) => {
    const newList = {
      ...(scenarios ?? []).find((sce) => sce?.uuid === value),
      isRecording: false,
      tempVideoUrl: '',
    };
    setScenarioWithSelector(newList || {});
  };

  const isDisabled = useMemo(() => {
    if (
      scenariosLength === 0 ||
      !final_decision ||
      selectedScenario?.response?.status !== null ||
      ['IN_PROGRESS', 'QUEUED', 'UNRATED', 'FAILED'].includes(status_ai) ||
      isEditing
    )
      return true;

    return false;
  }, [scenariosLength, final_decision, selectedScenario?.response, status_ai, isEditing]);

  const isNewDisabled = useMemo(() => {
    if (
      scenariosLength === 0 ||
      !final_decision ||
      selectedScenario?.response?.status !== null ||
      ['IN_PROGRESS', 'QUEUED', 'UNRATED', 'FAILED'].includes(status_ai)
    )
      return true;

    return false;
  }, [scenariosLength, final_decision, selectedScenario?.response, status_ai]);

  const navigateNext = () => {
    history.push({
      pathname: `/realplay-review/${uuid}/submit`,
      state: {
        prevPath: `${location.pathname}${location.search ? location.search : ''}`,
      },
    });
  };

  const handlePass = () => {
    const scenarioResponse = {
      ai_rating: true,
      comment,
      review_decision: final_decision,
      criteria_ratings,
      summary,
    };
    reviewScenario(
      {
        assignment_uuid,
        response_uuid: selectedScenario?.response?.uuid,
        body: scenarioResponse,
      },
      {
        onSuccess: () => {
          const newList = {
            ...(scenarios ?? []).find((sce) => sce?.uuid === value),
            isRecording: false,
            tempVideoUrl: '',
          };
          setScenarioWithSelector(newList || {});
          if (scenarios.length === scenarioSelectedIndex + 1) navigateNext();
        },
      }
    );
  };

  const handleFail = () => {
    const scenarioResponse = {
      ai_rating: true,
      comment,
      review_decision: final_decision,
      criteria_ratings,
      summary,
    };

    reviewScenario(
      {
        assignment_uuid,
        response_uuid: selectedScenario?.response?.uuid,
        body: scenarioResponse,
      },
      {
        onSuccess: () => {
          const newList = {
            ...(scenarios ?? []).find((sce) => sce?.uuid === value),
            isRecording: false,
            tempVideoUrl: '',
          };
          setScenarioWithSelector(newList || {});
          if (scenarios.length === scenarioSelectedIndex + 1) navigateNext();
        },
      }
    );
  };

  const confirmAll = useMemo(
    () => scenarios?.every((item) => item.response.status && item?.response?.average != null),
    [scenarios]
  );

  const generateRoleplayHeader = (roleplay) => {
    const dateString = formattedDate();
    const readableDate = endDate ? new Date(endDate).toDateString().substring(3) : dateString;
    const userNameHeader =
      assigned_to_user?.first_name && assigned_to_user?.last_name
        ? `${assigned_to_user.first_name} ${assigned_to_user.last_name}`
        : 'Alex Morgan';

    if (roleplay?.toLowerCase?.() === 'objection handling') {
      return `${userNameHeader} - Handling ${
        selectedScenario.name
      } Objection with ${client?.name ?? "AI"}, ${customer_context?.role ?? ""} on ${readableDate}`;
    }

    return `${userNameHeader} - Interactive Roleplay "${roleplay}" ${dateString}`;
  };

  return {
    endDate,
    navigate,
    PATHS,
    reset,
    title,
    formattedDate,
    onSelectedValue,
    scenariosLength,
    value,
    options,
    isEditing,
    selectedScenario,
    scenarios,
    final_decision,
    confirmAll,
    handlePass,
    handleFail,
    isDisabled,
    navigateNext,
    isNewDisabled,
    self_assigned,
    assigned_to_user,
    generateRoleplayHeader,
  };
};

export default RealPlayQualifiedLogic;
